import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Snackbar,
  Divider,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import yaml from 'js-yaml';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  fetchTopicsByCourse, 
  createCourse, 
  updateCourse, 
  deleteCourse,
  moveTopic,
  copyTopic,
  createTopic,
  createTopicsBulk,
  deleteTopic,
  reorderTopics
} from '../utils/api';
import SuggestedTopics from '../components/SuggestedTopics';

// Topic Tree Component for each course column
const TopicTreeItem = ({ 
  topic, 
  path, 
  count, 
  counts,
  depth = 0, 
  expanded, 
  onToggleExpand, 
  onDelete, 
  onAddSubtopic,
  onCopyTopic,
  isDragging,
  topicIds = {}
}) => {
  const hasChildren = topic && (
    (Array.isArray(topic) && topic.length > 0) || 
    (typeof topic === 'object' && Object.keys(topic).length > 0)
  );
  
  const handleToggle = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggleExpand(path);
    }
  };

  const handleAddSubtopic = (e) => {
    e.stopPropagation();
    onAddSubtopic(path);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    const topicId = topicIds[path];
    if (topicId) {
      onDelete(path, topicId);
    } else {
      console.error('Topic ID not found for path:', path, 'Available IDs:', Object.keys(topicIds));
    }
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    onCopyTopic(path);
  };

  const topicName = path.split('/').pop();

  return (
    <Box sx={{ mb: 0.5 }}>
      <Card 
        variant="outlined" 
        sx={{ 
          opacity: isDragging ? 0.5 : 1,
          bgcolor: depth === 0 ? 'primary.light' : depth === 1 ? 'secondary.light' : 'background.paper',
          borderLeft: depth === 0 ? '4px solid' : '2px solid',
          borderLeftColor: depth === 0 ? 'primary.main' : depth === 1 ? 'secondary.main' : 'divider',
        }}
      >
        <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragIndicatorIcon fontSize="small" sx={{ cursor: 'grab' }} />
            
            <IconButton size="small" onClick={handleToggle}>
              {hasChildren ? (
                expanded ? <FolderOpenIcon /> : <FolderIcon />
              ) : (
                <DescriptionIcon />
              )}
            </IconButton>
            
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {topicName} 
              <Chip 
                label={count || 0} 
                size="small" 
                sx={{ ml: 1, height: 20 }} 
              />
            </Typography>
            
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            
            {depth < 2 && (
              <IconButton size="small" onClick={handleAddSubtopic}>
                <AddIcon fontSize="small" />
              </IconButton>
            )}
            
            <IconButton 
              size="small" 
              onClick={handleDelete} 
              color="error"
              disabled={isDragging}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
      
      {hasChildren && expanded && (
        <Box sx={{ ml: 3, mt: 0.5 }}>
          {Array.isArray(topic) ? (
            // For arrays (subsubtopics), use the actual string values
            topic.map((subName) => {
              const subPath = `${path}/${subName}`;
              return (
                <TopicTreeItem
                  key={subPath}
                  topic={null}  // Leaf nodes have no children
                  path={subPath}
                  count={counts[subPath] || 0}
                  counts={counts}
                  depth={depth + 1}
                  expanded={expanded}
                  onToggleExpand={onToggleExpand}
                  onDelete={onDelete}
                  topicIds={topicIds}
                  onAddSubtopic={onAddSubtopic}
                  onCopyTopic={onCopyTopic}
                  isDragging={isDragging}
                />
              );
            })
          ) : (
            // For objects (topics/subtopics), use Object.entries
            Object.entries(topic).map(([subName, subTopic]) => {
              const subPath = `${path}/${subName}`;
              return (
                <TopicTreeItem
                  key={subPath}
                  topic={subTopic}
                  path={subPath}
                  count={counts[subPath] || 0}
                  counts={counts}
                  depth={depth + 1}
                  expanded={expanded}
                  onToggleExpand={onToggleExpand}
                  onDelete={onDelete}
                  topicIds={topicIds}
                  onAddSubtopic={onAddSubtopic}
                  onCopyTopic={onCopyTopic}
                  isDragging={isDragging}
                />
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
};

// Course Column Component
const CourseColumn = ({ 
  course, 
  topics, 
  counts,
  expandedTopics,
  onToggleExpand,
  onEditCourse,
  onDeleteCourse,
  onAddTopic,
  onDeleteTopic,
  onCopyTopic,
  onDropTopic,
  isCollapsed,
  onToggleCollapse
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEditCourse(course);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDeleteCourse(course.id);
  };

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Paper 
        sx={{ 
          height: isCollapsed ? 'auto' : 'calc(100vh - 200px)', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Course Header */}
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
          onClick={() => onToggleCollapse(course.id)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small" 
              sx={{ color: 'inherit' }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse(course.id);
              }}
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
            <Typography variant="h6">{course.name}</Typography>
          </Box>
          <Box>
            <IconButton 
              size="small" 
              sx={{ color: 'inherit' }}
              onClick={(e) => {
                e.stopPropagation();
                onAddTopic(course.id);
              }}
            >
              <AddIcon />
            </IconButton>
            <IconButton 
              size="small" 
              sx={{ color: 'inherit' }}
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e);
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEdit}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
              </MenuItem>
              <MenuItem onClick={handleDelete}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Topics Area */}
        {!isCollapsed && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Suggested Topics Section */}
            <Box sx={{ px: 2, pt: 2, overflow: 'auto', maxHeight: '40%' }}>
              <SuggestedTopics
                courseId={course.id}
                courseName={course.name}
                onAddTopic={onAddTopic}
              />
            </Box>
            
            {/* Topics Drag and Drop Area */}
            <Droppable droppableId={`course-${course.id}`} type="topic">
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'background.default',
                    transition: 'background-color 0.2s'
                  }}
                >
                {Object.entries(topics || {}).map(([topicName, topicData], index) => (
                <Draggable 
                  key={topicName} 
                  draggableId={`${course.id}-${topicName}`} 
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <TopicTreeItem
                        topic={topicData}
                        path={topicName}
                        count={counts[topicName] || 0}
                        counts={counts}
                        depth={0}
                        expanded={expandedTopics[topicName]}
                        onToggleExpand={onToggleExpand}
                        onDelete={onDeleteTopic}
                        topicIds={course.topic_ids || {}}
                        onAddSubtopic={(path) => onAddTopic(course.id, path)}
                        onCopyTopic={(path) => onCopyTopic(path, course.id)}
                        isDragging={snapshot.isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
          </Box>
        )}
      </Paper>
    </Grid>
  );
};

// Main Component
const CourseTopicManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [collapsedCourses, setCollapsedCourses] = useState({});
  
  // Operation loading states
  const [operationLoading, setOperationLoading] = useState({
    createCourse: false,
    updateCourse: false,
    deleteCourse: false,
    createTopic: false,
    deleteTopic: false,
    moveTopic: false,
    copyTopic: false,
    importYaml: false
  });
  
  // Dialog states
  const [courseDialog, setCourseDialog] = useState({ open: false, course: null });
  const [topicDialog, setTopicDialog] = useState({ open: false, courseId: null, parentPath: null, suggestedName: '' });
  const [copyDialog, setCopyDialog] = useState({ open: false, path: null, sourceCourseId: null });
  const [importDialog, setImportDialog] = useState({ open: false, yamlContent: '', fileName: '' });
  
  // YAML validation state
  const [yamlValidation, setYamlValidation] = useState({ 
    isValid: true, 
    error: null,
    warnings: [],
    parsedData: null 
  });
  
  // Check permissions
  useEffect(() => {
    if (!user || (user.role !== 'TUTOR' && user.role !== 'ADMIN')) {
      navigate('/');
    }
  }, [user, navigate]);

  // Warn about unsaved operations
  useEffect(() => {
    const isAnyOperationLoading = Object.values(operationLoading).some(loading => loading);
    
    const handleBeforeUnload = (e) => {
      if (isAnyOperationLoading) {
        e.preventDefault();
        e.returnValue = 'Operations are in progress. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [operationLoading]);

  // Validate YAML when content changes
  useEffect(() => {
    if (importDialog.open && importDialog.yamlContent) {
      // Debounce validation to avoid too many parse attempts while typing
      const timer = setTimeout(() => {
        validateYaml(importDialog.yamlContent);
      }, 300);
      
      return () => clearTimeout(timer);
    } else if (!importDialog.yamlContent) {
      // Reset validation when content is cleared
      setYamlValidation({ 
        isValid: true, 
        error: null, 
        warnings: [],
        parsedData: null 
      });
    }
  }, [importDialog.yamlContent, importDialog.open, courses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to parse error messages
  const parseError = (err) => {
    if (err.response?.data?.detail) {
      return err.response.data.detail;
    }
    if (err.response?.data?.message) {
      return err.response.data.message;
    }
    if (err.message) {
      return err.message;
    }
    return 'An unexpected error occurred. Please try again.';
  };

  // Helper function to set operation loading state
  const setOpLoading = (operation, isLoading) => {
    setOperationLoading(prev => ({ ...prev, [operation]: isLoading }));
  };

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchTopicsByCourse();
      setCourses(response.data.courses);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load courses and topics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Course operations
  const handleSaveCourse = async (name) => {
    // Validation
    if (!name || !name.trim()) {
      setError('Course name cannot be empty');
      return;
    }
    
    const trimmedName = name.trim();
    
    // Check if course name already exists (for new courses)
    if (!courseDialog.course) {
      const existingCourse = courses.find(c => 
        c.name.toLowerCase() === trimmedName.toLowerCase()
      );
      if (existingCourse) {
        setError(`A course named "${existingCourse.name}" already exists`);
        return;
      }
    }
    
    const isUpdate = !!courseDialog.course;
    setOpLoading(isUpdate ? 'updateCourse' : 'createCourse', true);
    
    try {
      if (isUpdate) {
        await updateCourse(courseDialog.course.id, trimmedName);
        setSuccess(`Course "${trimmedName}" updated successfully`);
      } else {
        await createCourse(trimmedName);
        setSuccess(`Course "${trimmedName}" created successfully`);
      }
      setCourseDialog({ open: false, course: null });
      loadData();
    } catch (err) {
      const errorMsg = parseError(err);
      if (errorMsg.includes('already exists')) {
        setError(`Course name "${trimmedName}" is already taken. Please choose a different name.`);
      } else {
        setError(errorMsg);
      }
    } finally {
      setOpLoading(isUpdate ? 'updateCourse' : 'createCourse', false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    const course = courses.find(c => c.id === courseId);
    if (!course) return;
    
    // Check if course has topics with questions
    const hasQuestions = Object.values(course.topic_counts || {}).some(count => count > 0);
    
    const confirmMsg = hasQuestions 
      ? `Course "${course.name}" appears to have questions. Deletion will fail if any questions exist. Continue?`
      : `Are you sure you want to delete course "${course.name}"?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setOpLoading('deleteCourse', true);
    
    try {
      await deleteCourse(courseId);
      setSuccess(`Course "${course.name}" deleted successfully`);
      loadData();
    } catch (err) {
      const errorMsg = parseError(err);
      if (errorMsg.includes('has associated questions') || errorMsg.includes('questions exist')) {
        setError(
          `Cannot delete course "${course.name}" because it contains questions. ` +
          `Please delete all questions first or move them to another course.`
        );
      } else {
        setError(`Failed to delete course: ${errorMsg}`);
      }
    } finally {
      setOpLoading('deleteCourse', false);
    }
  };

  // Topic operations
  const handleAddTopic = (courseId, parentPath = null, suggestedName = '') => {
    setTopicDialog({ open: true, courseId, parentPath, suggestedName });
  };

  const handleSaveTopic = async (name) => {
    // Validation
    if (!name || !name.trim()) {
      setError('Topic name cannot be empty');
      return;
    }
    
    const trimmedName = name.trim();
    
    // Validate characters (no forward slashes allowed in topic names)
    if (trimmedName.includes('/')) {
      setError('Topic name cannot contain forward slashes (/)');
      return;
    }
    
    const path = topicDialog.parentPath ? `${topicDialog.parentPath}/${trimmedName}` : trimmedName;
    const course = courses.find(c => c.id === topicDialog.courseId);
    
    // Check if topic already exists in this course
    const existingTopicId = course?.topic_ids?.[path];
    if (existingTopicId) {
      setError(`Topic "${path}" already exists in course "${course.name}"`);
      return;
    }
    
    setOpLoading('createTopic', true);
    
    try {
      // If there's a parent path, we need to ensure all parent topics exist
      if (topicDialog.parentPath) {
        const pathParts = topicDialog.parentPath.split('/');
        let currentPath = '';
        
        // Create each parent level if it doesn't exist
        for (const part of pathParts) {
          currentPath = currentPath ? `${currentPath}/${part}` : part;
          
          // Check if this path exists in the course
          const existingTopicId = course?.topic_ids?.[currentPath];
          if (!existingTopicId) {
            // Parent doesn't exist, create it
            try {
              await createTopic(currentPath, topicDialog.courseId);
            } catch (parentErr) {
              const parentErrorMsg = parseError(parentErr);
              // If it already exists (race condition), that's fine
              if (!parentErrorMsg.includes('already exists')) {
                throw parentErr;
              }
            }
          }
        }
      }
      
      // Now create the actual topic
      await createTopic(path, topicDialog.courseId);
      const level = path.split('/').length;
      const typeLabel = level === 1 ? 'Topic' : level === 2 ? 'Subtopic' : 'Subsubtopic';
      setSuccess(`${typeLabel} "${trimmedName}" created successfully in "${course?.name}"`);
      setTopicDialog({ open: false, courseId: null, parentPath: null, suggestedName: '' });
      loadData();
    } catch (err) {
      const errorMsg = parseError(err);
      if (errorMsg.includes('already exists')) {
        setError(`Topic "${path}" already exists in course "${course?.name}". Each topic path must be unique within a course.`);
      } else if (errorMsg.includes('Parent path') && errorMsg.includes('does not exist')) {
        setError('Failed to create parent topic. Please try again.');
      } else {
        setError(`Failed to create topic: ${errorMsg}`);
      }
    } finally {
      setOpLoading('createTopic', false);
    }
  };

  const handleDeleteTopic = async (path, topicId) => {
    if (!topicId) {
      setError('Could not find topic ID for deletion. Please refresh and try again.');
      return;
    }

    // Find which course this topic belongs to
    let topicCourse = null;
    for (const course of courses) {
      if (course.topic_ids && Object.values(course.topic_ids).includes(topicId)) {
        topicCourse = course;
        break;
      }
    }

    // Check if topic has questions
    const questionCount = topicCourse?.topic_counts?.[path] || 0;
    const hasQuestions = questionCount > 0;
    
    const confirmMsg = hasQuestions 
      ? `Topic "${path}" has ${questionCount} question${questionCount > 1 ? 's' : ''}. Deletion will fail. Continue anyway?`
      : `Delete topic "${path}"? This will also delete all subtopics.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setOpLoading('deleteTopic', true);
    
    try {
      await deleteTopic(topicId);
      const level = path.split('/').length;
      const typeLabel = level === 1 ? 'Topic' : level === 2 ? 'Subtopic' : 'Subsubtopic';
      setSuccess(`${typeLabel} "${path}" deleted successfully`);
      loadData();
    } catch (err) {
      const errorMsg = parseError(err);
      if (errorMsg.includes('has associated questions') || errorMsg.includes('because it has')) {
        const count = errorMsg.match(/\d+/)?.[0] || 'some';
        setError(
          `Cannot delete "${path}" because it has ${count} associated questions. ` +
          `Please delete or move the questions first.`
        );
      } else {
        setError(`Failed to delete topic: ${errorMsg}`);
      }
    } finally {
      setOpLoading('deleteTopic', false);
    }
  };

  const handleCopyTopic = (path, sourceCourseId) => {
    setCopyDialog({ open: true, path, sourceCourseId });
  };

  const handleConfirmCopy = async (targetCourseId) => {
    const sourceCourse = courses.find(c => c.id === copyDialog.sourceCourseId);
    const targetCourse = courses.find(c => c.id === targetCourseId);
    const topicId = sourceCourse?.topic_ids?.[copyDialog.path];
    
    if (!topicId) {
      setError('Could not find topic ID. Please refresh and try again.');
      return;
    }
    
    setOpLoading('copyTopic', true);
    
    try {
      await copyTopic(topicId, targetCourseId, true);
      const level = copyDialog.path.split('/').length;
      const typeLabel = level === 1 ? 'topic' : level === 2 ? 'subtopic' : 'subsubtopic';
      setSuccess(
        `Successfully copied ${typeLabel} "${copyDialog.path}" ` +
        `from "${sourceCourse?.name}" to "${targetCourse?.name}"`
      );
      setCopyDialog({ open: false, path: null, sourceCourseId: null });
      loadData();
    } catch (err) {
      const errorMsg = parseError(err);
      if (errorMsg.includes('already exists')) {
        setError(
          `Cannot copy to "${targetCourse?.name}" because topic "${copyDialog.path}" already exists there. ` +
          `Please rename or delete the existing topic first.`
        );
      } else {
        setError(`Failed to copy topic: ${errorMsg}`);
      }
    } finally {
      setOpLoading('copyTopic', false);
    }
  };

  // Drag and drop
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination || 
        (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }

    const sourceCourseId = parseInt(source.droppableId.replace('course-', ''));
    const destCourseId = parseInt(destination.droppableId.replace('course-', ''));

    if (sourceCourseId !== destCourseId) {
      // Moving between courses
      const [courseIdStr, ...pathParts] = draggableId.split('-');
      const topicPath = pathParts.join('-'); // Rejoin in case topic path has dashes
      
      const sourceCourse = courses.find(c => c.id === sourceCourseId);
      const destCourse = courses.find(c => c.id === destCourseId);
      const topicId = sourceCourse?.topic_ids?.[topicPath];
      
      if (!topicId) {
        setError('Could not find topic ID. Please refresh and try again.');
        return;
      }
      
      // Check if topic has questions
      const questionCount = sourceCourse?.topic_counts?.[topicPath] || 0;
      const warningMsg = questionCount > 0
        ? ` This will also move ${questionCount} question${questionCount > 1 ? 's' : ''}.`
        : '';
      
      if (window.confirm(
        `Move topic "${topicPath}" from "${sourceCourse?.name}" to "${destCourse?.name}"?${warningMsg}`
      )) {
        setOpLoading('moveTopic', true);
        
        try {
          await moveTopic(topicId, destCourseId);
          setSuccess(
            `Successfully moved "${topicPath}" to "${destCourse?.name}"` +
            (questionCount > 0 ? ` along with ${questionCount} questions` : '')
          );
          loadData();
        } catch (err) {
          const errorMsg = parseError(err);
          if (errorMsg.includes('already exists')) {
            setError(
              `Cannot move to "${destCourse?.name}" because topic "${topicPath}" already exists there. ` +
              `Please rename or delete the existing topic first.`
            );
          } else {
            setError(`Failed to move topic: ${errorMsg}`);
          }
        } finally {
          setOpLoading('moveTopic', false);
        }
      }
    } else {
      // Reordering within same course
      const course = courses.find(c => c.id === sourceCourseId);
      if (!course || !course.topics) return;
      
      // Extract the topic path from the draggableId
      const [courseIdStr, ...pathParts] = draggableId.split('-');
      const movedTopicPath = pathParts.join('-');
      
      // Get all root-level topic paths in their current order
      const rootTopicPaths = Object.keys(course.topics);
      
      // Remove the moved topic from its current position
      const filteredPaths = rootTopicPaths.filter(path => path !== movedTopicPath);
      
      // Insert it at the new position
      const newOrderedPaths = [
        ...filteredPaths.slice(0, destination.index),
        movedTopicPath,
        ...filteredPaths.slice(destination.index)
      ];
      
      // Optimistically update the UI
      const newCourses = courses.map(c => {
        if (c.id === sourceCourseId) {
          // Reorder the topics object
          const newTopics = {};
          newOrderedPaths.forEach(path => {
            newTopics[path] = course.topics[path];
          });
          return { ...c, topics: newTopics };
        }
        return c;
      });
      setCourses(newCourses);
      
      // Call the API to persist the new order
      handleReorderTopics(null, newOrderedPaths, sourceCourseId); // null parent_path for root topics
    }
  };

  // Topic reordering
  const handleReorderTopics = async (parentPath, orderedPaths, courseId) => {
    try {
      await reorderTopics(parentPath, orderedPaths, courseId);
      setSuccess('Topic order updated successfully');
      // Note: We already updated the UI optimistically, so no need to reload
    } catch (err) {
      const errorMsg = parseError(err);
      setError(`Failed to update topic order: ${errorMsg}`);
      // Reload to restore correct order on failure
      loadData();
    }
  };

  // UI helpers
  const toggleExpand = (path) => {
    setExpandedTopics(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const toggleCourseCollapse = (courseId) => {
    setCollapsedCourses(prev => ({ ...prev, [courseId]: !prev[courseId] }));
  };

  // YAML validation function
  const validateYaml = (content) => {
    if (!content.trim()) {
      setYamlValidation({ 
        isValid: true, 
        error: null, 
        warnings: [],
        parsedData: null 
      });
      return;
    }

    try {
      // Parse YAML
      const data = yaml.load(content);
      
      const validation = {
        isValid: true,
        error: null,
        warnings: [],
        parsedData: data
      };

      // Check if it's an object
      if (!data || typeof data !== 'object') {
        validation.isValid = false;
        validation.error = 'YAML must contain a valid object structure';
        setYamlValidation(validation);
        return;
      }

      // Check for required fields
      if (!data.course) {
        validation.isValid = false;
        validation.error = 'Missing required field: "course"';
      } else if (typeof data.course !== 'string') {
        validation.isValid = false;
        validation.error = '"course" must be a string';
      } else if (!data.course.trim()) {
        validation.isValid = false;
        validation.error = 'Course name cannot be empty';
      }

      if (!data.topics) {
        validation.isValid = false;
        validation.error = validation.error 
          ? validation.error + ' and "topics"' 
          : 'Missing required field: "topics"';
      } else if (typeof data.topics !== 'object' || Array.isArray(data.topics)) {
        validation.isValid = false;
        validation.error = validation.error 
          ? validation.error 
          : '"topics" must be an object (not an array)';
      }

      // If basic structure is valid, check for warnings
      if (validation.isValid) {
        // Check if course already exists
        const existingCourse = courses.find(c => 
          c.name.toLowerCase() === data.course.trim().toLowerCase()
        );
        if (existingCourse) {
          validation.warnings.push(`Course "${existingCourse.name}" already exists - import will fail`);
          validation.isValid = false;
          validation.error = `Course "${existingCourse.name}" already exists`;
        }

        // Validate topic names (no slashes)
        const validateTopicNames = (obj, path = '') => {
          if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
            for (const [key, value] of Object.entries(obj)) {
              if (key.includes('/')) {
                validation.warnings.push(`Invalid topic name "${key}" - cannot contain forward slashes`);
                validation.isValid = false;
              }
              if (typeof value === 'object' && !Array.isArray(value)) {
                validateTopicNames(value, path ? `${path}/${key}` : key);
              } else if (Array.isArray(value)) {
                // Check array items
                value.forEach(item => {
                  if (typeof item === 'string' && item.includes('/')) {
                    validation.warnings.push(`Invalid subsubtopic name "${item}" - cannot contain forward slashes`);
                    validation.isValid = false;
                  }
                });
              }
            }
          }
        };

        if (data.topics) {
          validateTopicNames(data.topics);
        }

        // Count topics
        const countTopics = (obj) => {
          let count = 0;
          if (typeof obj === 'object' && obj !== null) {
            for (const [key, value] of Object.entries(obj)) {
              count++;
              if (typeof value === 'object' && !Array.isArray(value)) {
                count += countTopics(value);
              } else if (Array.isArray(value)) {
                count += value.length;
              }
            }
          }
          return count;
        };

        const topicCount = countTopics(data.topics);
        if (topicCount === 0) {
          validation.warnings.push('No topics defined - at least one topic is recommended');
        } else if (topicCount > 100) {
          validation.warnings.push(`Large number of topics (${topicCount}) - import may take some time`);
        }
      }

      setYamlValidation(validation);
    } catch (err) {
      // YAML parsing error
      let errorMsg = 'Invalid YAML format';
      if (err.message) {
        // Try to make the error message more user-friendly
        if (err.message.includes('unexpected end of the stream')) {
          errorMsg = 'Incomplete YAML - check for missing quotes or incomplete structures';
        } else if (err.message.includes('bad indentation')) {
          errorMsg = 'Invalid indentation - YAML requires consistent spacing';
        } else if (err.message.includes('unknown tag')) {
          errorMsg = 'Invalid YAML tag - check for special characters';
        } else {
          errorMsg = `YAML syntax error: ${err.message}`;
        }
      }
      
      setYamlValidation({
        isValid: false,
        error: errorMsg,
        warnings: [],
        parsedData: null
      });
    }
  };

  // YAML Import handlers
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.yml') || file.name.endsWith('.yaml')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImportDialog({ 
          open: true, 
          yamlContent: e.target.result, 
          fileName: file.name 
        });
      };
      reader.readAsText(file);
    } else {
      setError('Please select a valid YAML file (.yml or .yaml)');
    }
    // Reset file input
    event.target.value = '';
  };

  const handleImportYaml = async () => {
    // Use pre-validated data
    if (!yamlValidation.isValid || !yamlValidation.parsedData) {
      setError('Please fix validation errors before importing');
      return;
    }
    
    setOpLoading('importYaml', true);
    
    try {
      const data = yamlValidation.parsedData;

      // Create the course
      const courseResponse = await createCourse(data.course.trim());
      const courseId = courseResponse.data.id;
      
      // Collect all topic paths
      const allPaths = [];
      
      const collectPaths = (topics, parentPath = null) => {
        // Handle both object and array inputs
        if (Array.isArray(topics)) {
          // If topics is an array at this level, these are leaf nodes
          for (const topic of topics) {
            if (topic && typeof topic === 'string') {
              const path = parentPath ? `${parentPath}/${topic}` : topic;
              allPaths.push(path);
            }
          }
          return;
        }
        
        // Handle object (normal case)
        for (const [topicName, subtopics] of Object.entries(topics)) {
          const path = parentPath ? `${parentPath}/${topicName}` : topicName;
          allPaths.push(path);
          
          // If subtopics exist and is an object, recurse
          if (subtopics && typeof subtopics === 'object' && !Array.isArray(subtopics)) {
            collectPaths(subtopics, path);
          } else if (Array.isArray(subtopics)) {
            // If it's an array, these are leaf nodes (subsubtopics)
            for (let i = 0; i < subtopics.length; i++) {
              const subsubtopic = subtopics[i];
              if (subsubtopic && typeof subsubtopic === 'string') {
                allPaths.push(`${path}/${subsubtopic}`);
              }
            }
          }
        }
      };
      
      // Collect all paths
      collectPaths(data.topics);
      
      // Create all topics in one bulk request
      if (allPaths.length > 0) {
        await createTopicsBulk(allPaths, courseId);
      }
      
      setSuccess(`Course "${data.course}" imported successfully`);
      setImportDialog({ open: false, yamlContent: '', fileName: '' });
      loadData();
    } catch (err) {
      const errorMsg = parseError(err);
      if (errorMsg.includes('already exists')) {
        setError(`Import failed: ${errorMsg}. Please use unique names for courses and topics.`);
      } else {
        setError(`Failed to import YAML: ${errorMsg}`);
      }
    } finally {
      setOpLoading('importYaml', false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Course & Topic Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setImportDialog({ open: true, yamlContent: '', fileName: '' })}
          >
            Import YAML
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCourseDialog({ open: true, course: null })}
          >
            Add Course
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setError(null)}
          variant="filled"
        >
          <strong>Error:</strong> {error}
        </Alert>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={2}>
          {courses.map(course => (
            <CourseColumn
              key={course.id}
              course={course}
              topics={course.topics}
              counts={course.topic_counts || {}}
              expandedTopics={expandedTopics}
              onToggleExpand={toggleExpand}
              onEditCourse={(c) => setCourseDialog({ open: true, course: c })}
              onDeleteCourse={handleDeleteCourse}
              onAddTopic={handleAddTopic}
              onDeleteTopic={handleDeleteTopic}
              onCopyTopic={handleCopyTopic}
              onDropTopic={handleDragEnd}
              isCollapsed={collapsedCourses[course.id] || false}
              onToggleCollapse={toggleCourseCollapse}
            />
          ))}
        </Grid>
      </DragDropContext>

      {/* Course Dialog */}
      <Dialog 
        open={courseDialog.open} 
        onClose={() => !operationLoading.createCourse && !operationLoading.updateCourse && setCourseDialog({ open: false, course: null })}
      >
        <DialogTitle>{courseDialog.course ? 'Edit Course' : 'Add Course'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Course Name"
            fullWidth
            variant="outlined"
            defaultValue={courseDialog.course?.name || ''}
            disabled={operationLoading.createCourse || operationLoading.updateCourse}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !operationLoading.createCourse && !operationLoading.updateCourse) {
                handleSaveCourse(e.target.value);
              }
            }}
            id="course-name-input"
            helperText="Enter a unique course name"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCourseDialog({ open: false, course: null })}
            disabled={operationLoading.createCourse || operationLoading.updateCourse}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              const input = document.getElementById('course-name-input');
              handleSaveCourse(input.value);
            }} 
            variant="contained"
            disabled={operationLoading.createCourse || operationLoading.updateCourse}
            startIcon={
              (operationLoading.createCourse || operationLoading.updateCourse) && 
              <CircularProgress size={16} />
            }
          >
            {operationLoading.createCourse || operationLoading.updateCourse ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Topic Dialog */}
      <Dialog 
        open={topicDialog.open} 
        onClose={() => !operationLoading.createTopic && setTopicDialog({ open: false, courseId: null, parentPath: null, suggestedName: '' })}
      >
        <DialogTitle>Add Topic</DialogTitle>
        <DialogContent>
          {topicDialog.parentPath && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Parent: {topicDialog.parentPath}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Topic Name"
            fullWidth
            variant="outlined"
            id="topic-name-input"
            defaultValue={topicDialog.suggestedName || ''}
            disabled={operationLoading.createTopic}
            helperText="Enter a unique topic name (no forward slashes allowed)"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !operationLoading.createTopic) {
                handleSaveTopic(e.target.value);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setTopicDialog({ open: false, courseId: null, parentPath: null, suggestedName: '' })}
            disabled={operationLoading.createTopic}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              const input = document.getElementById('topic-name-input');
              handleSaveTopic(input.value);
            }} 
            variant="contained"
            disabled={operationLoading.createTopic}
            startIcon={operationLoading.createTopic && <CircularProgress size={16} />}
          >
            {operationLoading.createTopic ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Dialog */}
      <Dialog open={copyDialog.open} onClose={() => setCopyDialog({ open: false, path: null, sourceCourseId: null })}>
        <DialogTitle>Copy Topic to Course</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select target course for: {copyDialog.path}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {courses
              .filter(c => c.id !== copyDialog.sourceCourseId)
              .map(course => (
                <Button
                  key={course.id}
                  variant="outlined"
                  onClick={() => handleConfirmCopy(course.id)}
                >
                  {course.name}
                </Button>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialog({ open: false, path: null, sourceCourseId: null })}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import YAML Dialog */}
      <Dialog 
        open={importDialog.open} 
        onClose={() => {
          setImportDialog({ open: false, yamlContent: '', fileName: '' });
          setYamlValidation({ isValid: true, error: null, warnings: [], parsedData: null });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Import Course from YAML
          {importDialog.fileName && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              File: {importDialog.fileName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">
                Paste your YAML content below or upload a file:
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              component="label"
              sx={{ ml: 2 }}
            >
              Upload File
              <input
                type="file"
                hidden
                accept=".yml,.yaml"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>
          
          <Box sx={{ bgcolor: 'grey.100', p: 2, mb: 2, borderRadius: 1, fontSize: '0.875rem', fontFamily: 'monospace' }}>
            <pre>{`course: "Course Name"

topics:
  Topic1:
    Subtopic1:
      - Subsubtopic1
      - Subsubtopic2
    Subtopic2:
      - Subsubtopic3
  Topic2:
    Subtopic3: []`}</pre>
          </Box>
          
          <TextField
            autoFocus
            margin="dense"
            label="YAML Content"
            fullWidth
            multiline
            rows={10}
            variant="outlined"
            value={importDialog.yamlContent}
            onChange={(e) => setImportDialog({ ...importDialog, yamlContent: e.target.value })}
            placeholder="Paste your YAML content here..."
            disabled={operationLoading.importYaml}
            error={!!yamlValidation.error}
            helperText={yamlValidation.error || (yamlValidation.warnings.length > 0 && yamlValidation.warnings[0])}
          />
          
          {/* Validation feedback */}
          {importDialog.yamlContent && (
            <Box sx={{ mt: 2 }}>
              {yamlValidation.error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <strong>Error:</strong> {yamlValidation.error}
                </Alert>
              )}
              
              {yamlValidation.warnings.length > 0 && !yamlValidation.error && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <strong>Warnings:</strong>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                    {yamlValidation.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              
              {yamlValidation.isValid && yamlValidation.parsedData && !yamlValidation.error && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  <strong>Valid YAML!</strong> Ready to import course "{yamlValidation.parsedData.course}"
                  {yamlValidation.parsedData.topics && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Topics to create: {
                        (() => {
                          const countTopics = (obj) => {
                            let count = 0;
                            if (typeof obj === 'object' && obj !== null) {
                              for (const [key, value] of Object.entries(obj)) {
                                count++;
                                if (typeof value === 'object' && !Array.isArray(value)) {
                                  count += countTopics(value);
                                } else if (Array.isArray(value)) {
                                  count += value.length;
                                }
                              }
                            }
                            return count;
                          };
                          return countTopics(yamlValidation.parsedData.topics);
                        })()
                      }
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setImportDialog({ open: false, yamlContent: '', fileName: '' });
              setYamlValidation({ isValid: true, error: null, warnings: [], parsedData: null });
            }}
            disabled={operationLoading.importYaml}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImportYaml} 
            variant="contained"
            disabled={
              !importDialog.yamlContent.trim() || 
              !yamlValidation.isValid || 
              !!yamlValidation.error ||
              operationLoading.importYaml
            }
            startIcon={operationLoading.importYaml && <CircularProgress size={16} />}
          >
            {operationLoading.importYaml ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={5000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccess(null)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseTopicManagement;